import { capitalize } from "vue";

/**
 * Visual identity of an integration provider, shared by every surface that has
 * to name one (Integrations cards, the agent model picker, ModelCard).
 */
export interface ProviderConfig {
  name: string;
  label: string;
  icon: string;
  color: string; // Radix color name (e.g., "green", "orange", "blue")
}

/**
 * The providers an organization can bring its own key for.
 *
 * Single source of the human label. `capitalize()` on the raw key produces
 * "Openai" and "Elevenlabs", which is not how any of these vendors spell their
 * own name, and the Integrations page and the model picker used to disagree
 * because only one of them had the map.
 */
export const PROVIDERS: Record<string, ProviderConfig> = {
  openai: {
    name: "openai",
    icon: "simple-icons:openai",
    label: "OpenAI",
    color: "green",
  },
  anthropic: {
    name: "anthropic",
    icon: "simple-icons:anthropic",
    label: "Anthropic",
    color: "orange",
  },
  google: {
    name: "google",
    icon: "simple-icons:google",
    label: "Google AI Studio",
    color: "blue",
  },
  deepseek: {
    name: "deepseek",
    icon: "simple-icons:deepseek",
    label: "Deepseek",
    color: "sky",
  },
  alibaba: {
    name: "alibaba",
    icon: "simple-icons:alibabadotcom",
    label: "Alibaba",
    color: "amber",
  },
  elevenlabs: {
    name: "elevenlabs",
    icon: "simple-icons:elevenlabs",
    label: "Eleven Labs",
    color: "violet",
  },
};

/**
 * Providers that are not BYOK credential providers (so they stay out of the
 * Integrations cards) but still need a human name in the model picker.
 */
const PROVIDER_LABEL_OVERRIDES: Record<string, string> = {
  "opencode-go": "OpenCode Go",
};

/**
 * Human label for a provider or credential key. Falls back to `capitalize` for
 * anything the map does not know: model providers such as "qwen" or "meta"
 * never get a credential card but still get named in the picker.
 */
export function providerLabel(provider?: string | null): string {
  if (!provider) return "";
  const key = provider.trim().toLowerCase();
  return PROVIDERS[key]?.label ?? PROVIDER_LABEL_OVERRIDES[key] ?? capitalize(key);
}
